import { type JSX, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import QRCode from 'qrcode';

import { totpDisable, totpEnable, totpEnroll } from '../api.js';

/**
 * Account-security panel for turning TOTP 2FA on or off. Shown on the signed-in
 * "Your fleets" view. Three phases when enabling:
 *   1. enroll  → render the otpauth QR + the secret for manual entry
 *   2. enable  → user types a code from their app; we activate + show recovery
 *   3. done    → recovery codes shown once
 * When already enabled, offers a disable form (requires a fresh code).
 */
type View =
  | { kind: 'idle' }
  | { kind: 'enrolling' }
  | { kind: 'enroll-error'; message: string }
  | { kind: 'confirm'; secret: string; otpauthUri: string }
  | { kind: 'saved'; recoveryCodes: string[] }
  | { kind: 'disabling' };

export function TwoFactorSetup(props: {
  /** Whether the account currently has TOTP enabled. */
  enabled: boolean;
  /** Called after enable/disable changes state so the parent can refresh. */
  onChanged: () => void | Promise<void>;
}): JSX.Element {
  const [view, setView] = useState<View>({ kind: 'idle' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function startEnroll(): Promise<void> {
    setBusy(true);
    setErr(null);
    const res = await totpEnroll();
    setBusy(false);
    if (res.kind === 'ok') {
      setView({ kind: 'confirm', secret: res.secret, otpauthUri: res.otpauthUri });
      return;
    }
    if (res.kind === 'not-configured') {
      setView({ kind: 'enroll-error', message: 'Two-factor is not available yet — contact support.' });
      return;
    }
    setView({ kind: 'enroll-error', message: 'Could not start setup. Try again.' });
  }

  async function confirmEnable(secret: string): Promise<void> {
    const trimmed = code.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    setErr(null);
    const res = await totpEnable({ secret, code: trimmed });
    setBusy(false);
    if (res.kind === 'ok') {
      setCode('');
      setView({ kind: 'saved', recoveryCodes: res.recoveryCodes });
      await props.onChanged();
      return;
    }
    if (res.kind === 'bad-code') {
      setErr('That code is incorrect or expired — try the current one.');
      return;
    }
    setErr('Could not enable two-factor. Try again.');
  }

  async function confirmDisable(): Promise<void> {
    const trimmed = code.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    setErr(null);
    const res = await totpDisable(trimmed);
    setBusy(false);
    if (res.kind === 'ok') {
      setCode('');
      setView({ kind: 'idle' });
      await props.onChanged();
      return;
    }
    setErr('That code is incorrect or expired.');
  }

  return (
    <Box data-testid="twofactor-setup">
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        {props.enabled
          ? 'Two-factor is on. You enter a code from your authenticator app each time you sign in.'
          : 'Add a second step at sign-in with an authenticator app (Google Authenticator, Authy, 1Password, …).'}
      </Typography>

      {/* Already enabled → offer disable. */}
      {props.enabled && view.kind !== 'disabling' && (
        <Button color="inherit" variant="outlined" onClick={() => { setErr(null); setView({ kind: 'disabling' }); }} data-testid="twofactor-start-disable">
          Turn off two-factor
        </Button>
      )}

      {props.enabled && view.kind === 'disabling' && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); void confirmDisable(); }}>
          <Stack spacing={2}>
            <TextField
              label="Enter a current code to confirm"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              fullWidth
              inputProps={{ maxLength: 32, autoComplete: 'one-time-code', 'data-testid': 'twofactor-disable-code' }}
            />
            {err !== null && <Alert severity="error" role="alert">{err}</Alert>}
            <Button type="submit" variant="contained" color="error" disabled={busy} data-testid="twofactor-disable-submit" sx={{ alignSelf: 'flex-start' }}>
              {busy ? 'Turning off…' : 'Confirm turn off'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Not enabled → enroll flow. */}
      {!props.enabled && (view.kind === 'idle' || view.kind === 'enroll-error') && (
        <Stack spacing={2} alignItems="flex-start">
          {view.kind === 'enroll-error' && <Alert severity="error" role="alert" sx={{ width: '100%' }}>{view.message}</Alert>}
          <Button variant="contained" onClick={() => void startEnroll()} disabled={busy} data-testid="twofactor-enable-start">
            {busy ? 'Starting…' : 'Set up two-factor'}
          </Button>
        </Stack>
      )}

      {!props.enabled && view.kind === 'confirm' && (
        <TwoFactorConfirm
          secret={view.secret}
          otpauthUri={view.otpauthUri}
          code={code}
          onCode={setCode}
          busy={busy}
          err={err}
          onSubmit={() => void confirmEnable(view.secret)}
        />
      )}

      {view.kind === 'saved' && <RecoveryCodes codes={view.recoveryCodes} onDone={() => setView({ kind: 'idle' })} />}
    </Box>
  );
}

function TwoFactorConfirm(props: {
  secret: string;
  otpauthUri: string;
  code: string;
  onCode: (v: string) => void;
  busy: boolean;
  err: string | null;
  onSubmit: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, props.otpauthUri, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 200,
    }).catch(() => {
      /* secret is shown below as the fallback */
    });
  }, [props.otpauthUri]);

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); props.onSubmit(); }}>
      <Stack spacing={2} alignItems="flex-start">
        <Typography color="text.secondary" variant="body2">
          Scan this with your authenticator app, then enter the 6-digit code it shows to finish.
        </Typography>
        <Box component="canvas" ref={canvasRef} data-testid="twofactor-qr" aria-label="TOTP QR code" sx={{ borderRadius: 1, bgcolor: '#fff', p: 0.5 }} />
        <Typography variant="caption" color="text.secondary">
          Can’t scan? Enter this key manually: <Box component="code" sx={{ userSelect: 'all' }}>{props.secret}</Box>
        </Typography>
        <TextField
          label="Code from your app"
          value={props.code}
          onChange={(e) => props.onCode(e.target.value)}
          placeholder="123456"
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code', 'data-testid': 'twofactor-confirm-code' }}
        />
        {props.err !== null && <Alert severity="error" role="alert" sx={{ width: '100%' }}>{props.err}</Alert>}
        <Button type="submit" variant="contained" disabled={props.busy} data-testid="twofactor-confirm-submit">
          {props.busy ? 'Enabling…' : 'Enable two-factor'}
        </Button>
      </Stack>
    </Box>
  );
}

function RecoveryCodes(props: { codes: string[]; onDone: () => void }): JSX.Element {
  return (
    <Box role="status" data-testid="twofactor-recovery">
      <Alert severity="success" sx={{ mb: 2 }}>
        <strong>Save your recovery codes.</strong> Each works once if you lose your authenticator.
        They won’t be shown again.
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontFamily: 'monospace', mb: 2 }}>
        {props.codes.map((c) => (
          <Box component="code" key={c} sx={{ userSelect: 'all' }}>{c}</Box>
        ))}
      </Box>
      <Button variant="contained" onClick={props.onDone} data-testid="twofactor-recovery-done">
        I’ve saved them
      </Button>
    </Box>
  );
}
